import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { PreApplicationJourney } from '../applications/pre-application.types';
import {
  ClaudeTestResult,
  ComplianceReport,
  SeedDemoResult,
  SystemLogsResponse,
  SystemStatus,
  TestEmailResult,
  TestEmailTemplatesResponse,
} from './system.types';

/** The admin System Status, compliance and logs pages' reads and probes. */
@Injectable({ providedIn: 'root' })
export class SystemService {
  private readonly http = inject(HttpClient);

  getSystemStatus(): Observable<SystemStatus> {
    return this.http.get<SystemStatus>(`${environment.apiUrl}/api/admin/system-status/`);
  }

  getCompliance(): Observable<ComplianceReport> {
    return this.http.get<ComplianceReport>(`${environment.apiUrl}/api/admin/compliance/`);
  }

  /** The application journey with no record behind it (the User journey page). */
  getJourneyReference(): Observable<PreApplicationJourney> {
    return this.http.get<PreApplicationJourney>(
      `${environment.apiUrl}/api/admin/journey-reference/`,
    );
  }

  getSystemLogs(
    page: number,
    pageSize: number,
    level?: string,
    search?: string,
  ): Observable<SystemLogsResponse> {
    let params = new HttpParams().set('page', String(page)).set('page_size', String(pageSize));
    if (level) {
      params = params.set('level', level);
    }
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<SystemLogsResponse>(`${environment.apiUrl}/api/admin/system-logs/`, {
      params,
    });
  }

  getTestEmailTemplates(): Observable<TestEmailTemplatesResponse> {
    return this.http.get<TestEmailTemplatesResponse>(`${environment.apiUrl}/api/admin/test-email/`);
  }

  sendTestEmail(template: string, recipient: string): Observable<TestEmailResult> {
    return this.http.post<TestEmailResult>(`${environment.apiUrl}/api/admin/test-email/`, {
      template,
      recipient,
    });
  }

  testClaudeConnection(): Observable<ClaudeTestResult> {
    return this.http.post<ClaudeTestResult>(
      `${environment.apiUrl}/api/admin/system-status/claude-test/`,
      {},
    );
  }

  testNearConnection(): Observable<ClaudeTestResult> {
    return this.http.post<ClaudeTestResult>(
      `${environment.apiUrl}/api/admin/system-status/near-test/`,
      {},
    );
  }

  /**
   * Seed demo data into the current environment (FLOW-181).
   *
   * The body is deliberately empty: the seeder's destructive `--reset` path is
   * not exposed over HTTP. A refused environment comes back as 200 with
   * `status: 'error'`, not an HTTP error.
   */
  seedDemoData(): Observable<SeedDemoResult> {
    return this.http.post<SeedDemoResult>(
      `${environment.apiUrl}/api/admin/system-status/seed-demo/`,
      {},
    );
  }
}
